/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import { Loading } from '@app/components/common/Loading/Loading';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { CopyTwoTone, MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseSelect, Option } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { notificationController } from '@app/controllers/notificationController';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';
import { CreatePersonsV2, GetPersons, initValues, Person } from '@app/api/patient.api';
import { BaseModal } from '../common/BaseModal/BaseModal';
import { BaseDatePicker } from '../common/pickers/BaseDatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { FONT_SIZE } from '@app/styles/themes/constants';
import { Dates } from '@app/constants/Dates';
import { useSelector } from 'react-redux';
import { RootState } from '@app/store/store';
import { BaseCheckbox } from '../common/BaseCheckbox/BaseCheckbox';
import { VINMEC_CONDITION_PROGRAM, VINMEC_POLICY_PRINCIPAL } from '@app/constants/enums/priorities';
import Popover from 'antd/lib/popover';
import { Upload } from 'antd';
import { RelationshipCode } from '@app/interfaces/interfaces';
import { CampaignPackageDto, ProductsWithPackages, ProductWithPackages } from '@app/interfaces/voucher';
import {
  PackageSingleMultiSelectPicker,
  PersonPackageSelection,
  PackageOptionUi,
} from './PackageSingleMultiSelectPicker';
import { useLanguage } from '@app/hooks/useLanguage';

type SelectedPackageItem = CampaignPackageDto & {
  Id: string;
  Name: string;
  Description?: string;
  ord?: number;
  ProductId: string;
  Quantity: number;
};
type SelectedPackageSubmitItem = {
  ProductId: string;
  PackageId: string;
  Quantity: number;
};

const normalizePackageSelectionsForSubmit = (
  packageSelections: PersonPackageSelection[] = [],
): SelectedPackageSubmitItem[] => {
  const map = new Map<string, SelectedPackageSubmitItem>();

  (packageSelections || []).forEach((item) => {
    const productId = String(item?.ProductId || '');
    const packageId = String(item?.PackageId || '');

    if (!productId || !packageId) return;

    const key = buildPackageKey(productId, packageId);
    const current = map.get(key);

    if (current) {
      current.Quantity += 1;
    } else {
      map.set(key, {
        ProductId: productId,
        PackageId: packageId,
        Quantity: 1,
      });
    }
  });

  return Array.from(map.values());
};
const buildPackageKey = (productId: string, packageId: string) => `${productId}__${packageId}`;

const getPackageId = (pkg: any) => String(pkg?.Id ?? pkg?.id ?? '');
const getPackageName = (pkg: any) => pkg?.Name ?? pkg?.name ?? '';
const getPackageOrd = (pkg: any) => Number(pkg?.ord ?? pkg?.Ord ?? 0);
const getEvidenceUrl = (person: any) => person?.UrlEvidence || person?.EnvidenceImage || '';

const isImageUrl = (url?: string) => {
  if (!url) return false;
  const clean = url.split('?')[0].toLowerCase();
  return clean.endsWith('.png') || clean.endsWith('.jpg') || clean.endsWith('.jpeg') || clean.endsWith('.webp');
};
export const PersonFormV2: React.FC = () => {
  dayjs.locale('vi');

  const [persons, setPersons] = useState<Person[]>([]);
  const [register, setRegisterBy] = useState<string>();
  const [phone, setRegisterPhone] = useState<string>();
  const [isLoading, setLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [_idVoucher, setIdVoucher] = useState('');
  const [acceptRule1, setAcceptRule1] = useState(false);
  const [acceptRule2, setAcceptRule2] = useState(false);
  const [acceptRule3, setAcceptRule3] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = useForm();
  const isFirstRender = useRef(true);
  const { language } = useLanguage();
  const voucher = useSelector((state: RootState) => state.voucherSlice.voucher);

  const selectedPackagesByProduct = useSelector(
    (state: RootState) => (state.voucherSlice as any).selectedPackagesByProduct,
  ) as Record<string, SelectedPackageItem[]> | undefined;

  const productsWithPackages = useSelector((state: RootState) => (state.voucherSlice as any).productsWithPackages) as
    | ProductsWithPackages
    | undefined;

  const is2026 = voucher?.Category === 'PACKAGE';
  const ACCEPT = '.png,.jpg,.jpeg,.docx,.pdf';
  const MAX_MB = 10;

  const packageOptions = useMemo<PackageOptionUi[]>(() => {
    if (!is2026 || !selectedPackagesByProduct) return [];

    const productList = productsWithPackages?.Products || [];

    return Object.entries(selectedPackagesByProduct).flatMap(([productId, selectedPackages]) => {
      const product = productList.find((x: ProductWithPackages) => String(x.Id) === String(productId));

      return (selectedPackages || [])
        .filter((pkg: any) => Number(pkg?.Quantity || 0) > 0)
        .slice()
        .sort((a: any, b: any) => getPackageOrd(a) - getPackageOrd(b))
        .map((pkg: any) => ({
          ProductId: String(productId),
          ProductName: product?.Name ?? `${t('Sản phẩm')} ${productId}`,
          PackageId: getPackageId(pkg),
          PackageName: getPackageName(pkg),
          Quantity: Number(pkg.Quantity || 0),
        }));
    });
  }, [is2026, selectedPackagesByProduct, productsWithPackages, t]);

  // const selectedPackageIdsByProduct = useMemo(() => {
  //   const result: Record<string, string[]> = {};

  //   Object.entries(selectedPackagesByProduct || {}).forEach(([productId, packages]) => {
  //     result[productId] = (packages || []).flatMap((x: any) =>
  //       Array.from({ length: Number(x?.Quantity || 0) }, () => String(x.Id)),
  //     );
  //   });

  //   return result;
  // }, [selectedPackagesByProduct]);

  useEffect(() => {
    const hydrateForm = (payload: { register?: string; phone?: string; persons?: any[]; disabled?: boolean }) => {
      const nextRegister = payload.register || '';
      const nextPhone = payload.phone || '';
      const nextPersons = payload.persons || [];

      setRegisterBy(nextRegister);
      setRegisterPhone(nextPhone);
      setPersons(nextPersons as any);
      setIsDisabled(!!payload.disabled);

      form.setFieldsValue({
        register: nextRegister,
        phone: nextPhone,
        persons: nextPersons,
      });
    };

    const getPersonByVoucher = (code: string) => {
      GetPersons(code, language)
        .then((response) => {
          const mappedPersons = (response.Patients || []).map((x: any) => ({
            ...x,
            UrlEvidence: x?.UrlEvidence || x?.EnvidenceImage || '',
            SelectedPackages: x?.SelectedPackages || [],
            PackageSelections: x?.PackageSelections || [],
            PackageIds: x?.PackageIds || [],
            EvidenceFiles: [],
          }));

          hydrateForm({
            register: response.Voucher.RegisterBy,
            phone: response.Voucher.RegisterPhone,
            persons: mappedPersons,
            disabled: true,
          });
        })
        .catch((error) => {
          console.error('Error:', error);
          notificationController.error({
            message: t('Không tải được thông tin thành viên đã đăng ký'),
          });
          setIsDisabled(true);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (!isFirstRender.current) return;

    if (!voucher) {
      navigate('../');
      isFirstRender.current = false;
      return;
    }

    setIdVoucher(voucher.VoucherId);

    const isVoucherActive = Boolean((voucher as any)?.Active);

    if (isVoucherActive) {
      getPersonByVoucher(voucher.VoucherId);
    } else {
      hydrateForm({
        register: (voucher as any)?.RegisterBy || '',
        phone: (voucher as any)?.RegisterPhone || '',
        persons: [
          {
            ...initValues,
            PackageSelections: [],
            PackageIds: [],
            EvidenceFiles: [],
          } as any,
        ],
        disabled: false,
      });
      setLoading(false);
    }

    isFirstRender.current = false;
  }, [voucher, navigate, t, form]);

  const scrollToError = (errorInfo: any) => {
    const firstErrorField = errorInfo.errorFields[0];
    let name = firstErrorField.name[0];

    if (firstErrorField.name[0] === 'persons') {
      name = `${firstErrorField.name[0]}_${firstErrorField.name[1]}_${firstErrorField.name[2]}`;
    }

    const errorElement = document.querySelector(`[id="${name}"]`);
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // const expandPackageSelectionsToIds = (packageSelections: PersonPackageSelection[] = []) => {
  //   return (packageSelections || [])
  //     .filter((x) => x?.PackageId)
  //     .map((x) => String(x.PackageId));
  // };

  const normalizeClonePerson = (person: any) => {
    return {
      ...person,
      PackageSelections: [],
      PackageIds: [],
      EvidenceFiles: [],
    };
  };

  const handleSubmit = (values: { persons: Person[] }) => {
    const formPersons: any[] = values?.persons || [];

    if (is2026) {
      const registerCount = formPersons.filter((x) => x?.Relationship === RelationshipCode.Register).length;

      if (registerCount > 1) {
        notificationController.error({
          message: t('Chỉ được chọn 1 người đăng ký trong danh sách thành viên'),
        });
        return;
      }

      const usedQtyByPackageKey: Record<string, number> = {};

      formPersons.forEach((person: any) => {
        (person?.PackageSelections || []).forEach((s: any) => {
          const productId = String(s?.ProductId || '');
          const packageId = String(s?.PackageId || '');
          if (!productId || !packageId) return;

          const packageKey = buildPackageKey(productId, packageId);
          usedQtyByPackageKey[packageKey] = (usedQtyByPackageKey[packageKey] || 0) + 1;
        });
      });

      for (const pkg of packageOptions) {
        const packageKey = buildPackageKey(String(pkg.ProductId), String(pkg.PackageId));

        const used = usedQtyByPackageKey[packageKey] || 0;
        const allowed = Number(pkg.Quantity || 0);

        if (used > allowed) {
          notificationController.error({
            message: t('Gói {{name}} đã vượt quá số lượt được chọn', {
              name: pkg.PackageName,
            }),
          });
          return;
        }
      }
    }

    setPersons(formPersons);
    setIsShowModal(true);
  };

  // const buildCreatePersonsFormData = (
  //   voucherId: string,
  //   values: any,
  //   selectedPackageIdsMap?: Record<string, string[]>,
  // ) => {
  //   const rawPersons = values.persons || [];

  //   const personsJson = rawPersons.map((p: any) => {
  //     const { EvidenceFiles, PackageSelections, ...rest } = p;

  //     return {
  //       ...rest,
  //       PackageIds: expandPackageSelectionsToIds(PackageSelections || []),
  //     };
  //   });

  //   const fd = new FormData();
  //   fd.append('voucherId', voucherId);
  //   fd.append('register', values.register ?? '');
  //   fd.append('phone', values.phone ?? '');
  //   fd.append('personsJson', JSON.stringify(personsJson));
  //   fd.append('selectedPackageJson', JSON.stringify(selectedPackageIdsMap ?? {}));

  //   rawPersons.forEach((p: any, idx: number) => {
  //     const f = p?.EvidenceFiles?.[0]?.originFileObj as File | undefined;
  //     if (f) fd.append(`evidence_${idx}`, f);
  //   });

  //   return fd;
  // };
  const buildCreatePersonsFormData = (voucherId: string, values: any) => {
    const rawPersons = values.persons || [];

    const personsJson = rawPersons.map((p: any) => {
      const { EvidenceFiles, PackageSelections, PackageIds, SelectedPackages, ...rest } = p;

      return {
        ...rest,
        SelectedPackages: normalizePackageSelectionsForSubmit(PackageSelections || []),
      };
    });

    const fd = new FormData();
    fd.append('voucherId', voucherId);
    fd.append('register', values.register ?? '');
    fd.append('phone', values.phone ?? '');
    fd.append('personsJson', JSON.stringify(personsJson));

    // rawPersons.forEach((p: any, idx: number) => {
    //   const f = p?.EvidenceFiles?.[0]?.originFileObj as File | undefined;
    //   if (f) fd.append(`evidence_${idx}`, f);
    // });

    return fd;
  };
  const buildCreatePersonsPayload = (values: any) => {
  const rawPersons = values.persons || [];

  const persons = rawPersons.map((p: any) => {
    const { EvidenceFiles, PackageSelections, PackageIds, SelectedPackages, ...rest } = p;

    return {
      ...rest,
      SelectedPackages: normalizePackageSelectionsForSubmit(PackageSelections || []),
    };
  });

  return {
    register: values.register ?? '',
    phone: values.phone ?? '',
    personsJson: JSON.stringify(persons),
    selectedPackageJson: "", // hoặc gán dữ liệu thật nếu BE đang dùng field này
  };
};
  const createPer = async () => {
    if (isDisabled) return;

    const values = {
      persons: form.getFieldValue('persons') || [],
      phone: form.getFieldValue('phone'),
      register: form.getFieldValue('register'),
    };

    // const fd = buildCreatePersonsFormData(_idVoucher, values, selectedPackageIdsByProduct);
    //const fd = buildCreatePersonsFormData(_idVoucher, values);
    const payload = buildCreatePersonsPayload(values);
    setLoading(true);
    try {
      await CreatePersonsV2(_idVoucher, payload);
      navigate('../success');
      notificationController.success({ message: t('Thêm thông tin thành viên thành công') });
    } finally {
      setLoading(false);
      setIsShowModal(false);
    }
  };

  const allRulesAccepted = acceptRule1 && acceptRule2 && acceptRule3;


  const beforeUploadEvidence = (file: File) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const ok = ['png', 'jpg', 'jpeg', 'docx', 'pdf'].includes(ext);

    if (!ok) {
      notificationController.error({ message: t('Chỉ cho phép png, jpg/jpeg, docx, pdf') });
      return Upload.LIST_IGNORE;
    }

    if (file.size / 1024 / 1024 > MAX_MB) {
      notificationController.error({ message: t(`Dung lượng tối đa ${MAX_MB}MB`) });
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const normFile = (e: any) => (Array.isArray(e) ? e : e?.fileList ?? []);

  if (!voucher || isLoading) return <Loading />;

  return (
    <>
      <Auth.FormWrapper>
        <BaseForm
          form={form}
          disabled={isDisabled}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          initialValues={{ register, phone, persons }}
          onFinishFailed={(e) => scrollToError(e)}
        >
          <Auth.FormTitle>
            {!isDisabled ? (
              <>
                <span>{t('ĐĂNG KÝ THÀNH VIÊN CHƯƠNG TRÌNH')} </span>
                <br />
                <span>{t('CHĂM SÓC SỨC KHỎE VINMEC')}</span>
              </>
            ) : (
              <span>{t('THÔNG TIN THÀNH VIÊN CHƯƠNG TRÌNH CHĂM SÓC SỨC KHỎE VINMEC')}</span>
            )}
          </Auth.FormTitle>

          <BaseRow gutter={5} align="top" justify="space-between" style={{ width: '100%', padding: '10px' }}>
            <BaseCol xs={24} sm={12}>
              <Auth.FormItem
                name={'register'}
                rules={[{ required: true, message: t('Vui lòng điền họ và tên người đăng ký') }]}
              >
                <Auth.FormInput style={{ width: '100%' }} placeholder={t('Họ và tên người đăng ký')} />
              </Auth.FormItem>
            </BaseCol>

            <BaseCol xs={24} sm={12}>
              <Auth.FormItem
                name={'phone'}
                rules={[
                  { required: true, message: t('Vui lòng điền số điện thoại người đăng ký') },
                  {
                    pattern: /^[0-9]*$/,
                    message: t('Sai định dạng số điện thoại người đăng ký'),
                  },
                ]}
              >
                <Auth.FormInput style={{ width: '100%' }} placeholder={t('Số điện thoại người đăng ký')} />
              </Auth.FormItem>
            </BaseCol>
          </BaseRow>

          <Auth.FormList name="persons">
            {(fields, { add, remove }) => (
              <BaseRow style={{ gap: '10px' }}>
                {fields.map(({ key, name, ...restField }, index) => (
                  <BaseRow key={key} style={{ backgroundColor: 'white', padding: '10px' }}>
                    <Auth.FormTitle style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                      <>
                        {t('Thông tin thành viên')} {index + 1}
                      </>
                      <div>
                        {!isDisabled && fields.length < voucher?.LimitUser && (
                          <Popover content={t('Nhân bản')} trigger="hover">
                            <CopyTwoTone
                              onClick={() => add(normalizeClonePerson(form.getFieldValue(['persons', index])))}
                            />
                          </Popover>
                        )}{' '}
                        {!isDisabled && index > 0 && (
                          <Popover content={t('Xóa')} trigger="hover">
                            <MinusCircleOutlined style={{ color: 'red' }} onClick={() => remove(name)} />
                          </Popover>
                        )}
                      </div>
                    </Auth.FormTitle>

                    <BaseRow gutter={5} align="top" justify="space-between" style={{ width: '100%' }}>
                      <BaseCol xs={24} sm={8}>
                        <Auth.FormItem
                          {...restField}
                          name={[name, 'FullName']}
                          rules={[{ required: true, message: t('Vui lòng điền họ và tên') }]}
                        >
                          <Auth.FormInput style={{ width: '100%' }} placeholder={t('Họ và tên')} />
                        </Auth.FormItem>
                      </BaseCol>

                      <BaseCol xs={24} sm={8}>
                        <Auth.FormItem
                          name={[name, 'Gender']}
                          {...restField}
                          rules={[{ required: true, message: t('Vui lòng điền chọn giới tính') }]}
                        >
                          <BaseSelect style={{ width: '100%' }} placeholder={t('Chọn giới tính')}>
                            <Option key={'male'} value={1}>
                              {t('Nam')}
                            </Option>
                            <Option key={'female'} value={2}>
                              {t('Nữ')}
                            </Option>
                          </BaseSelect>
                        </Auth.FormItem>
                      </BaseCol>

                      <BaseCol xs={24} sm={8}>
                        <Auth.FormItem
                          name={[name, 'DateOfBirth']}
                          {...restField}
                          rules={[{ required: true, message: t('Vui lòng điền chọn ngày sinh') }]}
                        >
                          <BaseDatePicker
                            name="DateOfBirth"
                            style={{ width: '100%' }}
                            placeholder={t('Chọn ngày sinh')}
                            format={'DD/MM/YYYY'}
                            disabledDate={(date) => date > Dates.getToday()}
                          />
                        </Auth.FormItem>
                      </BaseCol>
                    </BaseRow>

                    <BaseRow gutter={5} align="top" justify="space-between" style={{ width: '100%' }}>
                      <BaseCol xs={24} sm={8}>
                        <Auth.FormItem
                          {...restField}
                          name={[name, 'IdentityNo']}
                          rules={[
                            { required: true, message: t('Vui lòng điền số CCCD/ định danh cá nhân') },
                            {
                              pattern: /^[A-Za-z0-9]*$/,
                              message: t('Sai định dạng số CCCD/ định danh cá nhân'),
                            },
                          ]}
                        >
                          <Auth.FormInput placeholder={t('Số CCCD/ định danh cá nhân')} />
                        </Auth.FormItem>
                      </BaseCol>

                      <BaseCol xs={24} sm={8}>
                        <Auth.FormItem
                          {...restField}
                          name={[name, 'PhoneNo']}
                          rules={[
                            { required: true, message: t('Vui lòng điền số điện thoại') },
                            {
                              pattern: /^[0-9]*$/,
                              message: t('Sai định dạng số điện thoại'),
                            },
                          ]}
                        >
                          <Auth.FormInput placeholder={t('Số điện thoại')} />
                        </Auth.FormItem>
                      </BaseCol>

                      <BaseCol xs={24} sm={8}>
                        <Auth.FormItem
                          {...restField}
                          name={[name, 'Email']}
                          rules={[
                            { required: true, message: t('Vui lòng điền địa chỉ Email') },
                            {
                              type: 'email',
                              message: t('Sai định dạng địa chỉ Email'),
                            },
                          ]}
                        >
                          <Auth.FormInput placeholder={t('Địa chỉ Email')} />
                        </Auth.FormItem>
                      </BaseCol>
                    </BaseRow>

                    <BaseRow gutter={5} align="top" style={{ width: '100%' }}>
                      <BaseCol span={24}>
                        <Auth.FormItem
                          {...restField}
                          name={[name, 'Address']}
                          rules={[{ required: true, message: t('Vui lòng điền địa chỉ cư trú') }]}
                        >
                          <Auth.FormInput style={{ width: '100%' }} placeholder={t('Địa chỉ cư trú')} />
                        </Auth.FormItem>
                      </BaseCol>
                    </BaseRow>

                    {is2026 && (
                      <>
                        {/* <BaseRow gutter={5} align="top" justify="space-between" style={{ width: '100%' }}>
                          <Auth.FormItem
                            noStyle
                            shouldUpdate={(prevValues: any, curValues: any) =>
                              prevValues?.persons !== curValues?.persons
                            }
                          >
                            {({ getFieldValue }) => {
                              const currentPersons = getFieldValue('persons') || [];
                              const currentRelationship = getFieldValue(['persons', name, 'Relationship']);

                              const registerSelectedIndex = currentPersons.findIndex(
                                (p: any) => p?.Relationship === RelationshipCode.Register,
                              );

                              const disableRegisterOption =
                                registerSelectedIndex !== -1 &&
                                registerSelectedIndex !== name &&
                                currentRelationship !== RelationshipCode.Register;

                              return (
                                <BaseCol xs={24} sm={12}>
                                  <Auth.FormItem {...restField} name={[name, 'Relationship']}>
                                    <BaseSelect allowClear placeholder={t('Mối quan hệ')}>
                                      <Option value={RelationshipCode.Register} disabled={disableRegisterOption}>
                                        {t('Người đăng ký')}
                                      </Option>
                                      <Option value={RelationshipCode.Father}>{t('Cha')}</Option>
                                      <Option value={RelationshipCode.Mother}>{t('Mẹ')}</Option>
                                       <Option value={RelationshipCode.Brother}>{t('Anh/Chị/Em')}</Option>
                                      <Option value={RelationshipCode.Grandfather}>{t('Ông')}</Option>
                                      <Option value={RelationshipCode.Grandmother}>{t('Bà')}</Option>
                                      <Option value={RelationshipCode.Child}>{t('Con')}</Option>
                                    </BaseSelect>
                                  </Auth.FormItem>
                                </BaseCol>
                              );
                            }}
                          </Auth.FormItem>

                        <Auth.FormItem noStyle shouldUpdate={(prevValues: any, curValues: any) => prevValues?.persons?.[name] !== curValues?.persons?.[name] }>
                            {({ getFieldValue }) => {
                              const relationship = getFieldValue(['persons', name, 'Relationship']);
                              const showEvidence =
                                relationship && relationship !== RelationshipCode.Register;

                              if (!showEvidence) return null;

                              const person = getFieldValue(['persons', name]) || {};
                              const evidenceUrl = getEvidenceUrl(person);

                              return (
                                <BaseCol xs={24} sm={12}>
                                  {!isDisabled ? (
                                    <Auth.FormItem
                                      {...restField}
                                      name={[name, 'EvidenceFiles']}
                                      valuePropName="fileList"
                                      getValueFromEvent={normFile}
                                      preserve={false}
                                      rules={[{ required: true, message: t('Vui lòng tải lên giấy tờ chứng minh') }]}
                                      extra={t('Chấp nhận: png, jpg/jpeg, docx, pdf (tối đa 10MB)')}
                                    >
                                      <Upload accept={ACCEPT} maxCount={1} beforeUpload={beforeUploadEvidence}>
                                        <BaseButton icon={<UploadOutlined />}>
                                          {t('Tải lên giấy tờ chứng minh')}
                                        </BaseButton>
                                      </Upload>
                                    </Auth.FormItem>
                                  ) : (
                                    <div style={{ width: '100%' }}>
                                      <div style={{ marginBottom: 8, fontWeight: 500 }}>
                                        {t('Giấy tờ chứng minh')}
                                      </div>

                                      {!evidenceUrl ? (
                                        <div style={{ color: '#999' }}>{t('Chưa có giấy tờ chứng minh')}</div>
                                      ) : isImageUrl(evidenceUrl) ? (
                                        <a href={evidenceUrl} target="_blank" rel="noreferrer">
                                          <img
                                            src={evidenceUrl}
                                            alt="evidence"
                                            style={{
                                              maxWidth: '220px',
                                              maxHeight: '160px',
                                              border: '1px solid #ddd',
                                              borderRadius: 6,
                                            }}
                                          />
                                        </a>
                                      ) : (
                                        <a href={evidenceUrl} target="_blank" rel="noreferrer">
                                          {t('Xem tệp giấy tờ chứng minh')}
                                        </a>
                                      )}
                                    </div>
                                  )}
                                </BaseCol>
                              );
                            }}
                          </Auth.FormItem>
                        </BaseRow> */}

                        <Auth.FormItem
                          noStyle
                          shouldUpdate={(prevValues: any, curValues: any) => prevValues?.persons !== curValues?.persons}
                        >
                          {({ getFieldValue }) => {
                            const currentPersons = getFieldValue('persons') || [];

                            const usedQtyByPackageKey: Record<string, number> = {};

                            currentPersons.forEach((p: any, idx: number) => {
                              if (idx >= name) return;

                              const selections = p?.PackageSelections || [];
                              selections.forEach((x: any) => {
                                const productId = String(x?.ProductId || '');
                                const packageId = String(x?.PackageId || '');
                                if (!productId || !packageId) return;

                                const packageKey = buildPackageKey(productId, packageId);
                                usedQtyByPackageKey[packageKey] = (usedQtyByPackageKey[packageKey] || 0) + 1;
                              });
                            });

                            const currentPerson = getFieldValue(['persons', name]) || {};
                            const selectedPackages = currentPerson?.SelectedPackages || [];

                            return (
                              <BaseRow gutter={5} align="top" style={{ width: '100%' }}>
                                <BaseCol span={24}>
                                  {!isDisabled ? (
                                    <Auth.FormItem
                                      {...restField}
                                      name={[name, 'PackageSelections']}
                                      rules={
                                        packageOptions.length > 0
                                          ? [
                                              {
                                                validator: (_, value) => {
                                                  const hasValue = Array.isArray(value) && value.length > 0;

                                                  return hasValue
                                                    ? Promise.resolve()
                                                    : Promise.reject(new Error(t('Vui lòng chọn gói sử dụng')));
                                                },
                                              },
                                            ]
                                          : []
                                      }
                                    >
                                      <PackageSingleMultiSelectPicker
                                        options={packageOptions}
                                        usedQtyByPackageKey={usedQtyByPackageKey}
                                        disabled={isDisabled}
                                      />
                                    </Auth.FormItem>
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        minHeight: 48,
                                        border: '1px solid #d9d9d9',
                                        borderRadius: 8,
                                        padding: '10px 12px',
                                        background: '#fafafa',
                                      }}
                                    >
                                      {selectedPackages.length > 0 ? (
                                        selectedPackages.map((pkg: any, idx: number) => (
                                          <div key={`${pkg.PackageId}_${idx}`} style={{ marginBottom: 6 }}>
                                            <strong>{pkg.PackageName}</strong>
                                            {' - '}
                                            {t('Đã chọn')}: {pkg.Quantity}
                                            {' | '}
                                            {t('Đã dùng')}: {pkg.UsedQuantity || 0}
                                            {' | '}
                                            {t('Còn lại')}: {pkg.RemainingQuantity || 0}
                                          </div>
                                        ))
                                      ) : (
                                        <span style={{ color: '#999' }}>{t('Chưa có gói sử dụng')}</span>
                                      )}
                                    </div>
                                  )}
                                </BaseCol>
                              </BaseRow>
                            );
                          }}
                        </Auth.FormItem>
                      </>
                    )}
                  </BaseRow>
                ))}

                <>
                  <BaseRow align="top" style={{ width: '100%', padding: '10px' }}>
                    {voucher && fields.length < voucher?.LimitUser && !isDisabled && (
                      <Auth.FormItem className="item-row" style={{ width: '100%' }}>
                        <BaseButton
                          type="dashed"
                          style={{ width: '100%' }}
                          onClick={() =>
                            add({
                              ...initValues,
                              PackageSelections: [],
                              PackageIds: [],
                              EvidenceFiles: [],
                            })
                          }
                          block
                          icon={<PlusOutlined />}
                        >
                          {t('Thêm thông tin thành viên')}
                        </BaseButton>
                      </Auth.FormItem>
                    )}

                    <Auth.FormItem className="item-row" style={{ width: '100%' }}>
                      <Auth.SubmitButton type="default" onClick={() => navigate('../')} disabled={1 !== 1}>
                        {t('Quay lại màn hình chính')}
                      </Auth.SubmitButton>
                    </Auth.FormItem>

                    {!isDisabled && (
                      <Auth.FormItem className="item-row" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <BaseCheckbox onChange={(e) => setAcceptRule1(e.target.checked)} checked={acceptRule1} style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: FONT_SIZE.xs, marginTop: 3.5 }}>
                            {t('Bằng việc xác nhận, bạn đã đồng ý với')}{' '}
                            <a href={VINMEC_CONDITION_PROGRAM} target="_blank" rel="noopener noreferrer">
                              {t('điều khoản chương trình Vinmec')}
                            </a>
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <BaseCheckbox onChange={(e) => setAcceptRule2(e.target.checked)} checked={acceptRule2} style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: FONT_SIZE.xs, marginTop: 3.5 }}>
                            {t('Bằng việc xác nhận, bạn đã đồng ý với')}{' '}
                            <a href={VINMEC_POLICY_PRINCIPAL} target="_blank" rel="noopener noreferrer">
                              {t('chính sách bảo vệ dữ liệu cá nhân của Vinmec')}
                            </a>
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                          <BaseCheckbox onChange={(e) => setAcceptRule3(e.target.checked)} checked={acceptRule3} style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: FONT_SIZE.xs, marginTop: 3.5 }}>
                            {t('Bằng việc xác nhận, bạn đã đồng ý rằng những thành viên được bạn cung cấp thông tin tại đây đã được bạn thông báo về các chính sách về')}{' '}
                            <a href={VINMEC_CONDITION_PROGRAM} target="_blank" rel="noopener noreferrer">
                              {t('điều khoản chương trình Vinmec')}
                            </a>
                            {t(' & ')}
                            <a href={VINMEC_POLICY_PRINCIPAL} target="_blank" rel="noopener noreferrer">
                              {t('chính sách bảo vệ dữ liệu cá nhân của Vinmec')}
                            </a>
                            {t(' và những cá nhân này đã đồng ý về việc cung cấp thông tin cá nhân, sử dụng dịch vụ theo các điều khoản được quy định tại các chính sách này')}
                          </span>
                        </div>
                        <Auth.SubmitButton type="primary" htmlType="submit" loading={isLoading} disabled={!allRulesAccepted}>
                          {t('Đăng ký')}
                        </Auth.SubmitButton>
                        <Auth.FormTitle className="note" style={{ textAlign: 'left' }}>
                          <i style={{ fontSize: FONT_SIZE.xs }}>
                            {t('Lưu ý')}:
                            {t(
                              'Biểu mẫu chỉ mở một lần duy nhất và không được bổ sung Thành viên khi đã xác nhận Đăng ký.',
                            )}
                          </i>
                        </Auth.FormTitle>
                      </Auth.FormItem>
                    )}
                  </BaseRow>
                </>
              </BaseRow>
            )}
          </Auth.FormList>
        </BaseForm>
      </Auth.FormWrapper>

      {isShowModal && (
        <BaseModal
          title={t('XÁC NHẬN ĐĂNG KÝ')}
          centered
          open={isShowModal}
          okText={t('Đăng ký')}
          cancelText={t('Chỉnh sửa')}
          onOk={() => createPer()}
          onCancel={() => setIsShowModal(false)}
          size="small"
          className="BaseModal"
        >
          <Auth.FormTitle className="warning">
            {persons.length == voucher?.LimitUser && (
              <i>
                {t(
                  'Biểu mẫu chỉ mở một lần duy nhất và không được bổ sung/ chỉnh sửa Thành viên khi đã xác nhận Đăng ký. Nhấn Đăng ký để xác nhận hoàn thành đăng ký. Nhấn Chỉnh sửa để điều chỉnh thông tin.',
                )}
              </i>
            )}

            {voucher && persons.length < voucher?.LimitUser && (
              <i>
                {t('Quý khách chưa đăng ký đủ ')} {voucher?.LimitUser}{' '}
                {t(
                  " thành viên. Biểu mẫu chỉ mở một lần duy nhất và không được bổ sung/ chỉnh sửa Thành viên khi đã xác nhận Đăng ký. Vui lòng nhấn 'Nhân bản' nếu muốn sao chép nhanh thông tin thành viên. Nhấn Đăng ký để xác nhận hoàn thành đăng ký. Nhấn Chỉnh sửa để điều chỉnh thông tin.",
                )}
              </i>
            )}
          </Auth.FormTitle>
        </BaseModal>
      )}
    </>
  );
};

export default PersonFormV2;
